import { SEOSegmentationPageType } from '@/__generated__/globalTypes';

interface GetPageTitleProps {
  defaultTitle: string;
  curPage: number;
  segmentNamePlural: string;
  city: string | null;
  state: string | null;
  pageType: string | null;
}

/**
 * Generates a title for a page based on provided properties.
 *
 * If the current page is 1, returns the default title.
 * Otherwise, creates a title with a "Page X - Top " prefix followed by the segment name.
 * For local page types, the title includes city and state information.
 *
 * @param {Object} params - The parameters for generating the page title
 * @param {string} params.defaultTitle - The default title to use for the first page
 * @param {number} params.curPage - The current page number
 * @param {string} params.segmentNamePlural - The plural name of the segment (e.g., "Doctors", "Restaurants")
 * @param {string|null} params.city - The city name, or null if not applicable
 * @param {string|null} params.state - The state name, or null if not applicable
 * @param {SEOSegmentationPageType|null} params.pageType - The type of page, or null if not applicable
 * @returns {string} The generated page title
 */
export const getPageTitle = ({
  defaultTitle,
  curPage,
  segmentNamePlural,
  city,
  state,
  pageType,
}: GetPageTitleProps) => {
  if (curPage === 1) return defaultTitle;

  const prefix = `Page ${curPage} - Top `;

  if (city === null || state === null || pageType === null) {
    return `${prefix}${segmentNamePlural}`;
  }

  return pageType === SEOSegmentationPageType.LOCAL
    ? `${prefix}${segmentNamePlural} in ${city}, ${state}`
    : `${prefix}${segmentNamePlural}`;
};
